import './index.css';
import { User, UserManager, WebStorageStateStore } from "oidc-client-ts";
import { useLayoutEffect, useRef, useState } from "react";
import axios from "axios";
import { AuthProvider, hasAuthParams, useAuth } from "react-oidc-context";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
//#region src/lib/utils/getUser.ts
const url$1 = import.meta.env.DEV ? import.meta.env.VITE_KEYCLOAK_SERVER_URI : window.__env.KEYCLOAK_SERVER_URI;
const realm$1 = import.meta.env.DEV ? import.meta.env.VITE_KEYCLOAK_REALM : window.__env.KEYCLOAK_REALM;
const clientId$1 = import.meta.env.DEV ? import.meta.env.VITE_KEYCLOAK_CLIENTID : window.__env.KEYCLOAK_CLIENTID;
function getUser() {
	const oidcStorage = sessionStorage.getItem(`oidc.user:${url$1}/auth/realms/${realm$1}:${clientId$1}`);
	if (!oidcStorage) return null;
	return User.fromStorageString(oidcStorage);
}
//#endregion
//#region src/lib/components/AuthAxiosInterceptors.tsx
/**
* Подставляет Bearer в axios, пока пользователь аутентифицирован.
* Используйте внутри {@link KeycloakOidcProvider} рядом с маршрутизатором, если не используете KeycloakAuth с ProtectedApp.
*/
function AuthAxiosInterceptors() {
	const auth = useAuth();
	const interceptorIdRef = useRef(null);
	useLayoutEffect(() => {
		if (!auth.isAuthenticated) return;
		interceptorIdRef.current = axios.interceptors.request.use((config) => {
			const user = getUser();
			if (user?.access_token) config.headers.Authorization = "Bearer " + user.access_token;
			return config;
		});
		return () => {
			if (interceptorIdRef.current !== null) {
				axios.interceptors.request.eject(interceptorIdRef.current);
				interceptorIdRef.current = null;
			}
		};
	}, [auth.isAuthenticated]);
	return null;
}
//#endregion
//#region src/lib/components/LoadingIndicator.module.css
var LoadingIndicator_module_default = { "loading": "sfkbHa_loading" };
//#endregion
//#region src/lib/components/LoadingIndicator.tsx
const LoadingIndicator = ({ className, ...props }) => {
	return /* @__PURE__ */ jsxs("figure", {
		...props,
		className: [LoadingIndicator_module_default.loading, className].filter(Boolean).join(" "),
		children: [
			/* @__PURE__ */ jsx("div", {}),
			/* @__PURE__ */ jsx("div", {}),
			/* @__PURE__ */ jsx("div", {}),
			/* @__PURE__ */ jsx("div", {})
		]
	});
};
//#endregion
//#region src/lib/components/ProtectedApp.tsx
const ProtectedApp = ({ children }) => {
	const auth = useAuth();
	const [hasTriedSignin, setHasTriedSignin] = useState(false);
	/**
	* Do auto sign in.
	*
	* See {@link https://github.com/authts/react-oidc-context?tab=readme-ov-file#automatic-sign-in}
	*/
	useLayoutEffect(() => {
		if (!hasAuthParams() && !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading && !hasTriedSignin) {
			auth.signinRedirect();
			setHasTriedSignin(true);
		}
	}, [auth, hasTriedSignin]);
	useLayoutEffect(() => {
		if (auth.error) {
			auth.signinRedirect();
			setHasTriedSignin(true);
		}
	}, [auth]);
	const LoadingElement = /* @__PURE__ */ jsx("main", {
		style: {
			background: "#F5F1F0",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			height: "100vh",
			width: "100vw",
			overflow: "hidden"
		},
		children: /* @__PURE__ */ jsx(LoadingIndicator, { style: {
			height: "1.5rem",
			width: "1.5rem",
			color: "#afafaf"
		} })
	});
	return /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx(AuthAxiosInterceptors, {}), auth.error ? LoadingElement : auth.isLoading ? LoadingElement : !auth.isAuthenticated ? LoadingElement : !auth?.user?.access_token ? LoadingElement : children] });
};
//#endregion
//#region src/lib/config.ts
const url = import.meta.env.DEV ? import.meta.env.VITE_KEYCLOAK_SERVER_URI : window.__env.KEYCLOAK_SERVER_URI;
const realm = import.meta.env.DEV ? import.meta.env.VITE_KEYCLOAK_REALM : window.__env.KEYCLOAK_REALM;
const clientId = import.meta.env.DEV ? import.meta.env.VITE_KEYCLOAK_CLIENTID : window.__env.KEYCLOAK_CLIENTID;
const userManager = new UserManager({
	authority: `${url}/auth/realms/${realm}`,
	client_id: clientId,
	redirect_uri: `${window.location.origin}${window.location.pathname}`,
	post_logout_redirect_uri: window.location.origin,
	userStore: new WebStorageStateStore({ store: window.sessionStorage }),
	monitorSession: true,
	accessTokenExpiringNotificationTimeInSeconds: 1
});
const onSigninCallback = () => {
	window.history.replaceState({}, document.title, window.location.pathname);
};
//#endregion
//#region src/lib/AuthProvider.tsx
/** Только OIDC-контекст и колбэк входа — для связки с собственным роутингом (например `beforeLoad` в TanStack Router). */
function KeycloakOidcProvider({ children }) {
	return /* @__PURE__ */ jsx(AuthProvider, {
		userManager,
		onSigninCallback,
		children
	});
}
/** OIDC + блокировка детей до успешного входа (классическая обёртка приложения). */
const KeycloakAuth = ({ children }) => /* @__PURE__ */ jsx(KeycloakOidcProvider, { children: /* @__PURE__ */ jsx(ProtectedApp, { children }) });
//#endregion
//#region src/lib/hooks/useAuthState.ts
const BLACKLIST_GGE_PERMISSIONS_BY_ROLE = {
	V: [
		"CREATE_PROJECT",
		"PROJECT_INFORMATION_EXPERTISE_EDIT",
		"MARK_AS_PRIMARY_MODEL",
		"MARK_AS_ENGINEERING",
		"ADD_USER_AND_ACCEPT_CHANGED",
		"DELETE_USER_EXPERTISE",
		"EDIT_ORGANIZATION_NAME",
		"PROJECT_ORGANIZATION_ADD",
		"EDIT_PROJECT_INFORMATION",
		"RENAME_FILE_FILESYSTEM",
		"DELETE_FILE_FILESYSTEM",
		"ARCHIVE_PROJECT",
		"ROLES_CREATE_PROJECT"
	],
	M: [
		"MARK_AS_PRIMARY_MODEL",
		"MARK_AS_ENGINEERING",
		"EDIT_ORGANIZATION_NAME",
		"PROJECT_ORGANIZATION_ADD",
		"ADD_USER_AND_ACCEPT_CHANGED",
		"ROLES_CREATE_PROJECT"
	],
	A: ["MARK_AS_ENGINEERING"],
	SA: []
};
const useAuthState = () => {
	const { user, signoutRedirect, removeUser } = useAuth();
	const profile = user?.profile;
	if (profile) {
		const userProfile = {
			id: profile.sub,
			username: profile.name,
			attributes: profile.attributes,
			lastName: profile.family_name,
			firstName: profile.given_name,
			givenName: profile.middle_name,
			email: profile.email,
			phone: profile.phone || "",
			roles: profile["global-role"],
			groups: profile.user_groups || [],
			preferredUsername: profile.preferred_username,
			permissionsBlacklist: null
		};
		if (import.meta.env.VITE_SYSTEM_TYPE === "GGE") {
			let role = "V";
			if (profile["global-role"]?.includes("ПЛАТФОРМА 3Д ПРОСМОТР")) role = "V";
			if (profile["global-role"]?.includes("ПЛАТФОРМА 3Д МОДЕРИРОВАНИЕ")) role = "M";
			if (profile["global-role"]?.includes("ПЛАТФОРМА 3Д АДМИНИСТРИРОВАНИЕ")) role = "A";
			if (profile["global-role"]?.includes("ПЛАТФОРМА 3Д СУПЕР АДМИНИСТРАТОР")) role = "SA";
			if (role) userProfile.permissionsBlacklist = BLACKLIST_GGE_PERMISSIONS_BY_ROLE[role];
		}
		return {
			currentUser: {
				...userProfile,
				position: ""
			},
			logout: signoutRedirect,
			removeUser
		};
	}
	return {
		currentUser: null,
		logout: signoutRedirect,
		removeUser
	};
};
//#endregion
export { AuthAxiosInterceptors, KeycloakAuth, KeycloakOidcProvider, getUser, useAuthState };

//# sourceMappingURL=index.js.map